import React, { useEffect, useState } from 'react';
import './styles';
import { PlusIcon } from '../../../../../assets/icons';
import PLTable from '../../../../../components/PL/PLTable/PLTable';
import UIController from '../../../../../components/UI/UIController';
import Helmet from 'react-helmet';
import UIModal from '../../../../../components/UI/UIModal';
import * as EmailValidator from 'email-validator';
import UISearchBar, { UI_SEARCH_BAR_TYPES } from '../../../../../components/UI/UISearchBar';
import UIOverlayCard from '../../../../../components/UI/UIOverlayCard';
import UIIconButton from '../../../../../components/UI/UIIconButton';
import UIListInputRow from '../../../../../components/UI/UIListInputRow';
import { EDITING_COMPANY_TABLE_TYPE } from '../../../../../components/PL/PLTable/config';
import { UI_LABELED_INPUT_PHONE_NUMBER_TYPE, UI_LABELED_INPUT_TEXT_TYPE } from '../../../../../components/UI/UILabeledInput/config';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import UILoadingPage from '../../../../../components/UI/UILoadingPage/UILoadingPage';
import { useSelector } from 'react-redux';
import { getFiltersProp } from '../../../../../redux/selectors/CurrentUserSelectors';
import { getPaginatedTableVariablesByTypeAndSearchTypeScript } from '../../../../../components/PL/PLTable/TypeScriptHelpers';
import { useLazyQuery } from '@apollo/react-hooks';
import { useApolloClient } from 'react-apollo';
import { CREATE_EDITING_COMPANY, GET_ADMIN_EDITING_COMPANIES } from './queries';
import { UI_ICON_BUTTON_FILL_TYPE } from '../../../../../components/UI/UIIconButton/config';

export default function AdminEditingCompanies({}: AdminEditingCompaniesProps) {
	const QUERY_LIMIT = 60;

	const history = useHistory();
	const client = useApolloClient();

	const [editingCompanies, setEditingCompanies] = useState<EditingCompanyType[]>();
	const [editingCompanyName, setEditingCompanyName] = useState<string>('');
	const [editingCompanyEmail, setEditingCompanyEmail] = useState<string>('');
	const [editingCompanyPhoneNumber, setEditingCompanyPhoneNumber] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showCreateEditingCompanyModal, setShowCreateEditingCompanyModal] = useState<boolean>(false);

	const filters = useSelector(getFiltersProp);
	let props = {
		filters,
		location
	};

	let baseVariables = { query: {}, isActive: true };
	let variables = getPaginatedTableVariablesByTypeAndSearchTypeScript(QUERY_LIMIT, EDITING_COMPANY_TABLE_TYPE, props, baseVariables);
	const [getEditingCompanies, { data: editingCompaniesData }] = useLazyQuery(GET_ADMIN_EDITING_COMPANIES, {
		variables
	});

	useEffect(() => {
		(async () => {
			await getEditingCompanies();
		})();
	}, []);

	useEffect(() => {
		(() => {
			if (editingCompaniesData) {
				const fetchedEditingCompanies: EditingCompanyType[] = editingCompaniesData['editingCompanies'];
				setEditingCompanies(fetchedEditingCompanies);
			}
		})();
	}, [editingCompaniesData]);

	const createEditingCompany = async () => {
		if (!isLoading) {
			if (editingCompanyName.trim().length === 0) {
				toast.info('Please enter a company name!');
				return;
			} else if (!EmailValidator.validate(editingCompanyEmail.trim())) {
				toast.info('Please enter a valid email!');
				return;
			} else if (editingCompanyPhoneNumber.trim().length === 0) {
				toast.info('Please enter the company contact phone number!');
				return;
			}
			setIsLoading(true);

			const response = client.mutate({
				mutation: CREATE_EDITING_COMPANY,
				variables: {
					name: editingCompanyName,
					email: editingCompanyEmail,
					phoneNumber: editingCompanyPhoneNumber
				}
			});
			const { createEditingCompany } = response['data'];
			if (createEditingCompany) {
				setShowCreateEditingCompanyModal(false);
				setIsLoading(false);
				toast.success(`Successfully created ${response['data'].createEditingCompany.name}!`);
				history.push(`/editing-company/${response['data'].createEditingCompany.id}`);
			} else {
				setIsLoading(false);
				toast.error('Error!');
			}
		}
	};

	const renderTabComponent = () => {
		return (
			<div className='general-header-pin'>
				<UISearchBar type={UI_SEARCH_BAR_TYPES.user} onSearchUpdated={getEditingCompanies} className='editingCompanyFilter' placeholder={'search by name...'} />
				<div className='editingCompanyFilter--add' onClick={() => setShowCreateEditingCompanyModal(true)}>
					<img alt={'Add'} className='editingCompanyFilter--add-plus' src={PlusIcon.darkGrey} />
				</div>
			</div>
		);
	};

	const renderCreateEditingCompanyModal = () => {
		const hideModal = () => setShowCreateEditingCompanyModal(false);

		return (
			<UIModal condition={showCreateEditingCompanyModal}>
				<UIOverlayCard hideModal={hideModal} title={'Create an editing company'}>
					<UIListInputRow
						containerStyle={{ margin: '8px 0', width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_TEXT_TYPE}
						onChangeValue={setEditingCompanyName}
						value={editingCompanyName}
						placeholder={'Company name...'}
					/>
					<UIListInputRow
						containerStyle={{ marginBottom: 8, width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_TEXT_TYPE}
						onChangeValue={setEditingCompanyEmail}
						value={editingCompanyEmail}
						placeholder={'Company email...'}
					/>
					<UIListInputRow
						containerStyle={{ width: '100%' }}
						style={{ width: '100%' }}
						type={UI_LABELED_INPUT_PHONE_NUMBER_TYPE}
						onChangeValue={setEditingCompanyPhoneNumber}
						value={editingCompanyPhoneNumber}
						placeholder={'Company phone number...'}
					/>
					<div style={{ marginTop: 16, display: 'flex', flexDirection: 'row' }}>
						<UIIconButton dark type={UI_ICON_BUTTON_FILL_TYPE} isLoading={isLoading} text={'create'} onClick={createEditingCompany} />
					</div>
				</UIOverlayCard>
			</UIModal>
		);
	};

	const renderContent = () => {
		if (!editingCompanies) return <UILoadingPage />;
		return (
			<>
				{renderTabComponent()}
				<PLTable
					type={EDITING_COMPANY_TABLE_TYPE}
					limit={QUERY_LIMIT}
					updateTable={getEditingCompanies}
					data={editingCompanies}
					onSelect={(editingCompany) => history.push('/editing-company/' + editingCompany.id)}
				/>
				{renderCreateEditingCompanyModal()}
			</>
		);
	};

	return (
		<UIController>
			<Helmet>
				<title>Editing Companies | Pineapple Labs</title>
				<meta property='og:title' content='Editing Companies | Pineapple Labs' />
			</Helmet>
			{renderContent()}
		</UIController>
	);
}
